import { create } from 'zustand';
import { toast } from 'react-hot-toast';
import {
  canAppendToQueue,
  canRemoveOrReorderQueue,
  formatCooldownRemaining,
  guestCooldownRemainingMs,
  normalizeQueueItem,
} from '../utils/queueLogic';
import { useRoleStore } from './roleStore';
import useEventStore from './eventStore';
import { isEventLive } from '../utils/eventStatus';

let queuePublisher = null;

export function setQueuePublisher(publisher) {
  queuePublisher = publisher;
}

function queueContext() {
  const { userId, hostId } = useRoleStore.getState();
  const eventData = useEventStore.getState().eventData;
  const guestRequestsEnabled = eventData?.settings?.guestRequestsEnabled === true;
  const eventLive = isEventLive(eventData);
  return { userId, hostId, guestRequestsEnabled, eventLive };
}

const useQueueStore = create((set, get) => ({
  queue: [],
  revision: -1,
  voteSkips: {},
  lastRequestTime: {},

  applyRemoteQueue: (items, revision) => {
    set({
      queue: Array.isArray(items) ? items : [],
      revision: Number(revision) || 0,
    });
  },

  /**
   * Single append path for every source (search, My Music, playlist seeding).
   * `silent` only suppresses toasts for bulk adds; all permission, duplicate and
   * cooldown checks still run.
   */
  addToQueue: (video, { silent = false } = {}) => {
    const state = get();
    const ctx = queueContext();
    const item = normalizeQueueItem(video, ctx.userId || video?.addedBy);
    const notifyError = (message) => {
      if (!silent) toast.error(message);
    };

    if (!item) {
      notifyError('Invalid song');
      return false;
    }

    if (!canAppendToQueue(ctx)) {
      notifyError(ctx.eventLive === false ? 'This party has ended' : 'Guest song requests are disabled');
      return false;
    }

    if (state.queue.some((song) => song.id === item.id)) {
      notifyError('This song is already in the queue');
      return false;
    }

    const now = Date.now();
    const role = useRoleStore.getState().userRole;
    if (role === 'guest') {
      const remaining = guestCooldownRemainingMs(state.lastRequestTime[item.addedBy], now);
      if (remaining > 0) {
        notifyError(`Please wait ${formatCooldownRemaining(remaining)} before requesting another song`);
        return false;
      }
    }

    set({
      queue: [...state.queue, item],
      lastRequestTime: { ...state.lastRequestTime, [item.addedBy]: now },
    });

    queuePublisher?.append(item);
    if (!silent) toast.success('Song added to queue');
    return true;
  },

  removeSong: (songId) => {
    if (!canRemoveOrReorderQueue(queueContext())) {
      toast.error("You don't have permission to remove this song");
      return false;
    }

    const index = get().queue.findIndex((song) => song.id === songId);
    if (index === -1) return false;

    set((state) => ({
      queue: state.queue.filter((song) => song.id !== songId),
      voteSkips: { ...state.voteSkips, [songId]: [] },
    }));

    queuePublisher?.remove(index);
    toast.success('Song removed from queue');
    return true;
  },

  moveItem: (fromIndex, toIndex) => {
    if (toIndex < 0) return false;
    if (!canRemoveOrReorderQueue(queueContext())) {
      toast.error('Only the host can reorder the queue');
      return false;
    }

    set((state) => {
      if (toIndex >= state.queue.length) return state;
      const next = [...state.queue];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return { queue: next };
    });

    queuePublisher?.reorder(fromIndex, toIndex);
    return true;
  },

  removePlaybackFailure: (songId) => {
    const index = get().queue.findIndex((song) => song.id === songId);
    if (index < 0) return { removed: false, next: null };
    const next = get().queue[index + 1] || null;
    set((state) => ({
      queue: state.queue.filter((song) => song.id !== songId),
      voteSkips: { ...state.voteSkips, [songId]: [] },
    }));
    queuePublisher?.remove(index);
    return { removed: true, next };
  },

  removeItem: (index) => {
    if (!canRemoveOrReorderQueue(queueContext())) {
      toast.error('Only the host can remove songs');
      return false;
    }

    set((state) => ({
      queue: state.queue.filter((_, i) => i !== index),
    }));

    queuePublisher?.remove(index);
    return true;
  },

  reorderQueue: (fromIndex, toIndex, userRole) => {
    if (userRole !== 'host' && !canRemoveOrReorderQueue(queueContext())) {
      toast.error('Only hosts can reorder the queue');
      return false;
    }
    return get().moveItem(fromIndex, toIndex);
  },

  voteToSkip: (songId, userId, totalGuests, skipThreshold) => {
    set((state) => {
      const currentVotes = state.voteSkips[songId] || [];
      if (currentVotes.includes(userId)) return state;

      const newVotes = [...currentVotes, userId];
      const votePercentage = (newVotes.length / totalGuests) * 100;

      if (votePercentage >= skipThreshold) {
        return {
          queue: state.queue.filter((s) => s.id !== songId),
          voteSkips: { ...state.voteSkips, [songId]: [] },
        };
      }

      return { voteSkips: { ...state.voteSkips, [songId]: newVotes } };
    });
  },

  clearQueue: () => set({ queue: [], voteSkips: {}, revision: -1 }),

  getVoteCount: (songId) => {
    const votes = get().voteSkips[songId] || [];
    return votes.length;
  },
}));

export default useQueueStore;
